import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  UserGroupIcon, 
  TrophyIcon, 
  ShoppingBagIcon, 
  DocumentIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    users: 0,
    competitions: 0,
    products: 0,
    articles: 0,
    orders: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const collections = ['users', 'competitions', 'products', 'articles'];
      const statsData = {};

      // Fetch basic collection counts from Firestore
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        statsData[collectionName] = snapshot.size;
      }

      // Fetch orders count from backend API (same as AdminOrders)
      try {
        if (currentUser) {
          const token = await currentUser.getIdToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/admin/orders`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        
          if (response.ok) {
            const data = await response.json();
            statsData.orders = data.orders ? data.orders.length : 0;
          } else {
            console.error('Failed to fetch orders count:', response.status);
            statsData.orders = 0;
          }
        } else {
          statsData.orders = 0;
        }
      } catch (error) {
        console.error('Error fetching orders count:', error);
        statsData.orders = 0;
      }

      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const quickActions = [
    {
      name: 'Manage Articles',
      description: 'Upload and manage Dauntless PDF articles',
      href: '/admin/articles',
      icon: DocumentIcon,
      color: 'bg-primary-500',
      count: stats.articles
    },
    {
      name: 'Manage Users',
      description: 'Approve and manage user accounts',
      href: '/admin/users',
      icon: UserGroupIcon,
      color: 'bg-blue-500',
      count: stats.users
    },
    {
      name: 'Manage Competitions',
      description: 'Create and manage track events',
      href: '/admin/competitions',
      icon: TrophyIcon,
      color: 'bg-green-500',
      count: stats.competitions
    },
    {
      name: 'Manage Products',
      description: 'Manage merchandise and products',
      href: '/admin/products',
      icon: ShoppingBagIcon,
      color: 'bg-purple-500',
      count: stats.products
    },
    {
      name: 'Manage Orders',
      description: 'View and process orders',
      href: '/admin/orders',
      icon: ClipboardDocumentListIcon,
      color: 'bg-orange-500',
      count: stats.orders
    },
    {
      name: 'Manage Programs',
      description: 'Manage program signups, attendance, and invoices',
      href: '/admin/programs',
      icon: AcademicCapIcon,
      color: 'bg-indigo-500',
      count: 0
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your track and field club</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.users}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrophyIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Competitions</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.competitions}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShoppingBagIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Products</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.products}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Articles</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.articles}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Orders</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.orders}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="card-hover card p-6 block"
              >
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${action.color} rounded-lg p-3`}>
                    <action.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {action.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {action.description}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {action.count} items
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
              <span>Admin dashboard accessed</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              <span>System initialized successfully</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
